import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components';
import Button from '../../../shared/components/Button';
import { useDepartmentRolePermissions, useSetDepartmentRolePermissions } from '../hooks/useDepartments';
import {
  countGranted,
  fromEntries,
  hasAction,
  isSameMatrix,
  setRow,
  toEntries,
  toggleAction,
  type MatrixState,
} from '../permissionsMatrix';
import { RESOURCES, RESOURCE_ACTIONS, enumLabel, type DepartmentRole } from '../types';

interface PermissionsMatrixModalProps {
  role: DepartmentRole;
  canManage: boolean;
  onClose: () => void;
}

/**
 * PE-2147 — resource rows × action checkboxes for one department role, saved
 * as a full replace via `PUT /department-roles/:id/permissions`. Grants land in
 * the assignee's JWT `p` claim on their next login (permissions are encoded at
 * token issue time), which is what `useClaimPermission` reads.
 */
export const PermissionsMatrixModal = ({ role, canManage, onClose }: PermissionsMatrixModalProps) => {
  const { data: entries, isLoading, error } = useDepartmentRolePermissions(role.id);
  const save = useSetDepartmentRolePermissions();
  const [saved, setSaved] = useState<MatrixState>({});
  const [state, setState] = useState<MatrixState>({});
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!entries) return;
    const next = fromEntries(entries);
    setSaved(next);
    setState(next);
  }, [entries]);

  const dirty = !isSameMatrix(saved, state);
  const visible = RESOURCES.filter((r) => !filter || r.includes(filter.trim().toUpperCase().replace(/\s+/g, '_')));

  const handleSave = async () => {
    await save.mutateAsync({ roleId: role.id, permissions: toEntries(state) });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-foreground">Permissions — {role.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {countGranted(state)} grant{countGranted(state) === 1 ? '' : 's'} across{' '}
              {Object.keys(state).length} resource{Object.keys(state).length === 1 ? '' : 's'}. Changes apply
              to assignees on their next sign-in.
            </p>
          </div>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter resources"
            className="px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none"
          />
        </div>

        {error && <p className="text-sm text-destructive">Could not load permissions.</p>}
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!isLoading && (
          <div className="overflow-auto max-h-[60vh] border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 px-3 font-medium">Resource</th>
                  {RESOURCE_ACTIONS.map((action) => (
                    <th key={action} className="py-2 px-2 font-medium text-center whitespace-nowrap">
                      {enumLabel(action)}
                    </th>
                  ))}
                  <th className="py-2 px-2 font-medium text-center">All</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((resource) => {
                  const rowAll = RESOURCE_ACTIONS.every((a) => hasAction(state, resource, a));
                  return (
                    <tr key={resource} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="py-1.5 px-3 whitespace-nowrap text-foreground">{enumLabel(resource)}</td>
                      {RESOURCE_ACTIONS.map((action) => (
                        <td key={action} className="py-1.5 px-2 text-center">
                          <input
                            type="checkbox"
                            aria-label={`${resource} ${action}`}
                            checked={hasAction(state, resource, action)}
                            disabled={!canManage}
                            onChange={() => setState((s) => toggleAction(s, resource, action))}
                          />
                        </td>
                      ))}
                      <td className="py-1.5 px-2 text-center">
                        <input
                          type="checkbox"
                          aria-label={`${resource} all actions`}
                          checked={rowAll}
                          disabled={!canManage}
                          onChange={() => setState((s) => setRow(s, resource, RESOURCE_ACTIONS, !rowAll))}
                        />
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={RESOURCE_ACTIONS.length + 2} className="py-4 text-center text-muted-foreground">
                      No resources match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {canManage ? 'Cancel' : 'Close'}
          </Button>
          {canManage && (
            <Button onClick={handleSave} disabled={!dirty || save.isPending || isLoading} loading={save.isPending}>
              Save permissions
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
